import { useId, type ReactNode } from 'react';
import styles from './FormField.module.css';

/** Props a field's control must spread to be labelled, described and validated. */
export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
}

export interface FormFieldProps {
  label: string;
  hint?: string | undefined;
  /** Inline error for this field; also flips the control to aria-invalid. */
  error?: string | undefined;
  children: (control: FieldControlProps) => ReactNode;
}

/**
 * A visible label above the control, an optional hint, and an inline error,
 * all wired to the control through `id` / `aria-describedby` / `aria-invalid`.
 *
 * Placeholders stay as format examples only: they vanish on the first keystroke,
 * so they cannot be the label (WCAG 3.3.2).
 */
export function FormField({ label, hint, error, children }: FormFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  // The error replaces the hint (the error copy already repeats the example).
  const describedBy = error ? errorId : hint ? hintId : '';

  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      {children({
        id,
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(error ? { 'aria-invalid': true as const } : {}),
      })}
      {error ? (
        <p id={errorId} className={styles.error}>
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
